import { type Component } from 'vue'
import type { Message as ChatMessage } from '@/store/chat'
import {
  Letter,
  LicenseType,
  PaymentReceipt,
  UploadFiles,
} from '../components/chat/widgets'

export interface WidgetResolution {
  component: Component
  props: Record<string, any>
}

/**
 * Determines which widget components to render based on the message content.
 * Can return multiple widgets for a single message.
 * @param message - The chat message object.
 * @returns An array of components to render and their props.
 */
export function useWidgetResolver(message: ChatMessage): WidgetResolution[] {
  const { json, text } = message
  const resolvedWidgets: WidgetResolution[] = []

  // Rule for multiple Payment Receipts from a data array
  if (
    Array.isArray(json?.data) &&
    json.data.every((item) => item.closedPayrollId)
  ) {
    json.data.forEach((item) => {
      resolvedWidgets.push({
        component: PaymentReceipt,
        props: { items: item }, // Pass each item individually to its own widget
      })
    })
    return resolvedWidgets
  }

  // Rule for multiple Letters from a data array
  if (
    Array.isArray(json?.data) &&
    json.data.every((item) => item.letterCertifConfig?.name)
  ) {
    json.data.forEach((item) => {
      resolvedWidgets.push({
        component: Letter,
        props: { items: item }, // Pass each item individually
      })
    })
    return resolvedWidgets
  }

  // Rule for single License Type selection
  if (text.includes('&lt;&lt;SELECCIONAR TIPO DE LICENCIA&gt;&gt')) {
    resolvedWidgets.push({ component: LicenseType, props: {} })
    return resolvedWidgets
  }

  // Rule for single Vacation File Upload
  if (text.includes('&lt;&lt;SUBIR ARCHIVO DE VACACIONES AQUÍ&gt;&gt')) {
    resolvedWidgets.push({
      component: UploadFiles,
      props: { isMultiple: false },
    })
    return resolvedWidgets
  }

  // Rule for single License File Upload
  if (text.includes('&lt;&lt;SUBIR ARCHIVOS DE LICENCIAS AQUÍ&gt;&gt')) {
    resolvedWidgets.push({
      component: UploadFiles,
      props: { isMultiple: true },
    })
    return resolvedWidgets
  }

  // No specific widget found
  return []
}
